import type { ActionEntry } from "@agent-native/core/server";

import applyDreamProposal from "./apply-dream-proposal.js";
import approveDispatchChange from "./approve-dispatch-change.js";
import approveVaultRequest from "./approve-vault-request.js";
import archiveWorkspaceApp from "./archive-workspace-app.js";
import askApp from "./ask_app.js";
import askAppStatus from "./ask_app_status.js";
import connectExternalAgent from "./connect-external-agent.js";
import createBrowserChatSession from "./create-browser-chat-session.js";
import createDreamReport from "./create-dream-report.js";
import createLinkToken from "./create-link-token.js";
import createPylonTicket from "./create-pylon-ticket.js";
import createVaultGrant from "./create-vault-grant.js";
import createVaultSecret from "./create-vault-secret.js";
import createWorkspaceResourceGrant from "./create-workspace-resource-grant.js";
import createWorkspaceResource from "./create-workspace-resource.js";
import createEmbedSession from "./create_embed_session.js";
import createWorkspaceAppEmbedSession from "./create_workspace_app_embed_session.js";
import deleteDestination from "./delete-destination.js";
import deleteStagedDataset from "./delete-staged-dataset.js";
import deleteVaultSecret from "./delete-vault-secret.js";
import deleteWorkspaceResource from "./delete-workspace-resource.js";
import denyVaultRequest from "./deny-vault-request.js";
import ensureDreamJob from "./ensure-dream-job.js";
import getAgentThreadDebug from "./get-agent-thread-debug.js";
import getAppCreationSettings from "./get-app-creation-settings.js";
import getDispatchSettings from "./get-dispatch-settings.js";
import getDreamSettings from "./get-dream-settings.js";
import getDream from "./get-dream.js";
import getVaultAccessSettings from "./get-vault-access-settings.js";
import getWorkspaceInfo from "./get-workspace-info.js";
import getWorkspaceResourceEffectiveContext from "./get-workspace-resource-effective-context.js";
import grantVaultSecretsToApp from "./grant-vault-secrets-to-app.js";
import grantWorkspaceResourcesToApp from "./grant-workspace-resources-to-app.js";
import importAgentPack from "./import-agent-pack.js";
import importAgent from "./import-agent.js";
import listAgentPack from "./list-agent-pack.js";
import listAgentRunFailures from "./list-agent-run-failures.js";
import listAgentThreadSources from "./list-agent-thread-sources.js";
import listAvailableWorkspaceTemplates from "./list-available-workspace-templates.js";
import listConnectedAgents from "./list-connected-agents.js";
import listCuratedWorkspaceTemplates from "./list-curated-workspace-templates.js";
import listDestinations from "./list-destinations.js";
import listDispatchApprovals from "./list-dispatch-approvals.js";
import listDispatchAudit from "./list-dispatch-audit.js";
import listDispatchOverview from "./list-dispatch-overview.js";
import listDispatchUsageMetrics from "./list-dispatch-usage-metrics.js";
import listDreamCandidates from "./list-dream-candidates.js";
import listDreams from "./list-dreams.js";
import listIntegrationsCatalog from "./list-integrations-catalog.js";
import listLinkedIdentities from "./list-linked-identities.js";
import listMcpAppAccess from "./list-mcp-app-access.js";
import listStagedDatasets from "./list-staged-datasets.js";
import listVaultAudit from "./list-vault-audit.js";
import listVaultGrants from "./list-vault-grants.js";
import listVaultRequests from "./list-vault-requests.js";
import listVaultSecretOptions from "./list-vault-secret-options.js";
import listVaultSecrets from "./list-vault-secrets.js";
import listWorkspaceApps from "./list-workspace-apps.js";
import listWorkspaceConnections from "./list-workspace-connections.js";
import listWorkspaceResourceGrants from "./list-workspace-resource-grants.js";
import listWorkspaceResourceOptions from "./list-workspace-resource-options.js";
import listWorkspaceResourcesForApp from "./list-workspace-resources-for-app.js";
import listWorkspaceResources from "./list-workspace-resources.js";
import listApps from "./list_apps.js";
import navigate from "./navigate.js";
import openApp from "./open_app.js";
import previewDreamProposal from "./preview-dream-proposal.js";
import previewWorkspaceResourceChange from "./preview-workspace-resource-change.js";
import providerApiCatalog from "./provider-api-catalog.js";
import providerApiDocs from "./provider-api-docs.js";
import providerApiRegister from "./provider-api-register.js";
import providerApiRequest from "./provider-api-request.js";
import queryStagedDataset from "./query-staged-dataset.js";
import readSlackThreadContext from "./read-slack-thread-context.js";
import rejectDispatchChange from "./reject-dispatch-change.js";
import rejectDreamProposal from "./reject-dream-proposal.js";
import remixWorkspaceTemplate from "./remix-workspace-template.js";
import removePendingWorkspaceApp from "./remove-pending-workspace-app.js";
import requestVaultSecret from "./request-vault-secret.js";
import resolveIntegrationSourceContext from "./resolve-integration-source-context.js";
import restoreStarterWorkspaceResources from "./restore-starter-workspace-resources.js";
import revokeVaultGrant from "./revoke-vault-grant.js";
import revokeWorkspaceResourceGrant from "./revoke-workspace-resource-grant.js";
import scaffoldWorkspaceApp from "./scaffold-workspace-app.js";
import searchAgentThreads from "./search-agent-threads.js";
import sendCodeAgentRemoteCommand from "./send-code-agent-remote-command.js";
import sendPlatformMessage from "./send-platform-message.js";
import setAppCreationSettings from "./set-app-creation-settings.js";
import setDispatchApprovalPolicy from "./set-dispatch-approval-policy.js";
import setDreamSettings from "./set-dream-settings.js";
import setMcpAppAccess from "./set-mcp-app-access.js";
import setVaultAccessSettings from "./set-vault-access-settings.js";
import startWorkspaceAppCreation from "./start-workspace-app-creation.js";
import syncVaultToApp from "./sync-vault-to-app.js";
import unarchiveWorkspaceApp from "./unarchive-workspace-app.js";
import updateVaultSecret from "./update-vault-secret.js";
import updateWorkspaceAppMetadata from "./update-workspace-app-metadata.js";
import updateWorkspaceResource from "./update-workspace-resource.js";
import upsertDestination from "./upsert-destination.js";
import viewScreen from "./view-screen.js";

/**
 * Dispatch's actions registered as a flat name→entry map. Imported by
 * `@agent-native/dispatch/server`'s side-effect block, which calls
 * `registerPackageActions(dispatchActions)` so the framework's action
 * loader picks them up.
 */
export const dispatchActions: Record<string, ActionEntry> = {
  "approve-dispatch-change": approveDispatchChange,
  "approve-vault-request": approveVaultRequest,
  "archive-workspace-app": archiveWorkspaceApp,
  ask_app: askApp,
  ask_app_status: askAppStatus,
  "connect-external-agent": connectExternalAgent,
  "create-link-token": createLinkToken,
  "create-pylon-ticket": createPylonTicket,
  "create-vault-grant": createVaultGrant,
  "create-vault-secret": createVaultSecret,
  "create-workspace-resource-grant": createWorkspaceResourceGrant,
  "create-workspace-resource": createWorkspaceResource,
  "create-dream-report": createDreamReport,
  "create-browser-chat-session": createBrowserChatSession,
  create_embed_session: createEmbedSession,
  "create-workspace-app-embed-session": createWorkspaceAppEmbedSession,
  "delete-staged-dataset": deleteStagedDataset,
  "delete-destination": deleteDestination,
  "delete-vault-secret": deleteVaultSecret,
  "delete-workspace-resource": deleteWorkspaceResource,
  "deny-vault-request": denyVaultRequest,
  "ensure-dream-job": ensureDreamJob,
  "get-app-creation-settings": getAppCreationSettings,
  "get-agent-thread-debug": getAgentThreadDebug,
  "get-dream": getDream,
  "get-dream-settings": getDreamSettings,
  "get-dispatch-settings": getDispatchSettings,
  "get-vault-access-settings": getVaultAccessSettings,
  "get-workspace-resource-effective-context":
    getWorkspaceResourceEffectiveContext,
  "get-workspace-info": getWorkspaceInfo,
  "grant-workspace-resources-to-app": grantWorkspaceResourcesToApp,
  "grant-vault-secrets-to-app": grantVaultSecretsToApp,
  "list-agent-thread-sources": listAgentThreadSources,
  "list-agent-run-failures": listAgentRunFailures,
  "list-available-workspace-templates": listAvailableWorkspaceTemplates,
  "list-curated-workspace-templates": listCuratedWorkspaceTemplates,
  "list-connected-agents": listConnectedAgents,
  "list-destinations": listDestinations,
  "list-dispatch-approvals": listDispatchApprovals,
  "list-dispatch-audit": listDispatchAudit,
  "list-dispatch-overview": listDispatchOverview,
  "list-dispatch-usage-metrics": listDispatchUsageMetrics,
  "list-dream-candidates": listDreamCandidates,
  "list-dreams": listDreams,
  "list-integrations-catalog": listIntegrationsCatalog,
  "list-linked-identities": listLinkedIdentities,
  "list-mcp-app-access": listMcpAppAccess,
  list_apps: listApps,
  "list-staged-datasets": listStagedDatasets,
  "list-vault-audit": listVaultAudit,
  "list-vault-grants": listVaultGrants,
  "list-vault-requests": listVaultRequests,
  "list-vault-secret-options": listVaultSecretOptions,
  "list-vault-secrets": listVaultSecrets,
  "list-workspace-apps": listWorkspaceApps,
  "list-workspace-connections": listWorkspaceConnections,
  "list-workspace-resource-options": listWorkspaceResourceOptions,
  "list-workspace-resource-grants": listWorkspaceResourceGrants,
  "list-workspace-resources-for-app": listWorkspaceResourcesForApp,
  "list-workspace-resources": listWorkspaceResources,
  "import-agent": importAgent,
  "import-agent-pack": importAgentPack,
  "list-agent-pack": listAgentPack,
  navigate: navigate,
  open_app: openApp,
  "apply-dream-proposal": applyDreamProposal,
  "preview-dream-proposal": previewDreamProposal,
  "preview-workspace-resource-change": previewWorkspaceResourceChange,
  "provider-api-catalog": providerApiCatalog,
  "provider-api-docs": providerApiDocs,
  "provider-api-register": providerApiRegister,
  "provider-api-request": providerApiRequest,
  "query-staged-dataset": queryStagedDataset,
  "read-slack-thread-context": readSlackThreadContext,
  "reject-dispatch-change": rejectDispatchChange,
  "reject-dream-proposal": rejectDreamProposal,
  "remove-pending-workspace-app": removePendingWorkspaceApp,
  "resolve-integration-source-context": resolveIntegrationSourceContext,
  "remix-workspace-template": remixWorkspaceTemplate,
  "request-vault-secret": requestVaultSecret,
  "revoke-vault-grant": revokeVaultGrant,
  "revoke-workspace-resource-grant": revokeWorkspaceResourceGrant,
  "restore-starter-workspace-resources": restoreStarterWorkspaceResources,
  "scaffold-workspace-app": scaffoldWorkspaceApp,
  "search-agent-threads": searchAgentThreads,
  "send-code-agent-remote-command": sendCodeAgentRemoteCommand,
  "send-platform-message": sendPlatformMessage,
  "set-app-creation-settings": setAppCreationSettings,
  "set-dispatch-approval-policy": setDispatchApprovalPolicy,
  "set-dream-settings": setDreamSettings,
  "set-mcp-app-access": setMcpAppAccess,
  "set-vault-access-settings": setVaultAccessSettings,
  "start-workspace-app-creation": startWorkspaceAppCreation,
  "sync-vault-to-app": syncVaultToApp,
  "unarchive-workspace-app": unarchiveWorkspaceApp,
  "update-workspace-app-metadata": updateWorkspaceAppMetadata,
  "update-vault-secret": updateVaultSecret,
  "update-workspace-resource": updateWorkspaceResource,
  "upsert-destination": upsertDestination,
  "view-screen": viewScreen,
};
